import { useCreateNote } from '../../hooks/useCreateNote'
import { NoteType } from '../note'
import { v4 as uuid } from 'uuid'
import {
  ImportLog,
  renderImportLogMarkdown,
  createImportLogFilename,
  shouldDisplayLog,
  validateImportLog
} from './log'

/**
 * Hook for creating import log notes when errors occur during import operations
 *
 * Uses the enhanced logging subsystem with selective display logic and security compliance.
 * Creates a structured markdown note with comprehensive feedback sections.
 *
 * The log note is created at root level for easy discovery by users.
 */
export function useCreateImportLogNote() {
  const createNote = useCreateNote()

  return (importLog: ImportLog) => {

    // Validate the import log
    if (!validateImportLog(importLog)) {
      console.error('Invalid import log provided:', importLog)
      return
    }

    // Apply selective display logic - only create log if there are errors or warnings
    if (!shouldDisplayLog(importLog)) {
      // eslint-disable-next-line no-console -- Intentional debug logging for import operations
      console.debug('Import log not displayed - no errors or warnings to show')
      return
    }

    const filename = createImportLogFilename()
    const content = renderImportLogMarkdown(importLog)

    createNote.mutate(
      {
        recordName: uuid(),
        noteType: NoteType.PROJECT_NOTE,
        // eslint-disable-next-line unicorn/no-null -- createNote expects string | null for database compatibility
        parent: null, // Create at root level for easy discovery
        filename,
        content,
      },
      {
        onSuccess: () => {
          // TODO: Replace with trackEvent when analytics are re-enabled
          // eslint-disable-next-line no-console -- Intentional debug logging for import operations
          console.debug('WEB - Import Log Created', {
            filesCount: importLog.filesAttempted.length,
            errorCount: importLog.errors.length,
            warningCount: importLog.warnings.length,
            hintCount: importLog.hints.length,
          })

          // Log note created successfully - user can find it in sidebar
          // eslint-disable-next-line no-console -- intentional logging for import operations
          console.log(`Import log note created: ${filename}`)
        },
        onError: (error) => {
          // Failed to create log note - fallback to console
          console.error('Failed to create import log note:', error)
          console.error('Import log content:', content)
        },
      }
    )
  }
}
